import { type ReactNode } from 'react';
import { XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PaginationBar, type PaginationBarProps } from '@/components/ui/pagination-bar';
import { cn } from '@/lib/utils';

const SKELETON_CARDS = 3;

interface Props<T> {
  rows: T[];
  rowKey: (row: T) => string;
  renderCard: (row: T) => ReactNode;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  empty: ReactNode;
  pagination?: PaginationBarProps;
  /** Which screen sizes show the list, and any grid columns. */
  className?: string;
}

/** The small-screen stand-in for DataTable, with the same loading, error, empty and paging states. */
export function IdCardList<T>({
  rows, rowKey, renderCard, loading, error, onRetry, empty, pagination, className,
}: Props<T>) {
  const body = loading ? (
    Array.from({ length: SKELETON_CARDS }).map((_, i) => (
      <div key={i} className="h-32 animate-pulse rounded-xl border border-border/60 bg-muted/40" />
    ))
  ) : rows.map((row) => <div key={rowKey(row)} className="min-w-0">{renderCard(row)}</div>);

  return (
    <div className={cn('space-y-2', className)}>
      {error ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-border/60 bg-card px-4 py-10 text-destructive">
          <XCircle className="h-8 w-8 opacity-60" />
          <span className="text-sm font-medium">Failed to load data</span>
          <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>
        </div>
      ) : !loading && rows.length === 0 ? (
        <div className="rounded-xl border border-border/60 bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          {empty}
        </div>
      ) : (
        // Two columns only from lg: at md the sidebar is already showing, which leaves cards too narrow.
        <div className="grid gap-2 lg:grid-cols-2">{body}</div>
      )}
      {pagination && !error && (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <PaginationBar {...pagination} />
        </div>
      )}
    </div>
  );
}
